// Consent from the page's CMP through the IAB TCF v2.2 JavaScript API
// (`__tcfapi('addEventListener', 2, cb)`; getTCData is deprecated in v2.2).
// One listener for the page's lifetime keeps the state current as the user
// changes their choices.

export interface Consent {
  /** 1 GDPR applies, 0 it does not, undefined unknown. */
  gdpr?: 0 | 1;
  tcString?: string;
  /** Whether the tag may keep an identifier on the device. */
  storage: boolean;
}

interface TCData {
  gdprApplies?: boolean;
  tcString?: string;
  eventStatus?: string;
  purpose?: { consents?: Record<string, boolean> };
  vendor?: { consents?: Record<string, boolean> };
}

type TcfApi = (cmd: string, version: number, cb: (data: TCData, ok: boolean) => void) => void;

// Waiting for a CMP that never answers would leave the slot empty, so the
// first request waits at most this long and goes out with what is known.
const FIRST_SIGNAL_MS = 500;

declare const __TCF_VENDOR_ID__: number | undefined;

// The platform's GVL id, set at build from TCF_VENDOR_ID. With it the
// identifier also needs the vendor consent; 0 until the platform is registered.
const VENDOR_ID = typeof __TCF_VENDOR_ID__ === "number" ? __TCF_VENDOR_ID__ : 0;

let current: Consent = { storage: true };
let ready: Promise<void> | null = null;

export function toConsent(d: TCData, vendorId: number = VENDOR_ID): Consent {
  if (d.gdprApplies === undefined) return { tcString: d.tcString || undefined, storage: true };
  if (!d.gdprApplies) return { gdpr: 0, tcString: d.tcString || undefined, storage: true };
  // Purpose 1 is "store and/or access information on a device".
  const vendor = !vendorId || d.vendor?.consents?.[vendorId] === true;
  return { gdpr: 1, tcString: d.tcString || undefined, storage: vendor && d.purpose?.consents?.["1"] === true };
}

// consentReady resolves once the CMP has reported, or after FIRST_SIGNAL_MS,
// or at once when the page has no CMP.
export function consentReady(w: Window = window): Promise<void> {
  if (ready) return ready;
  const api = (w as unknown as { __tcfapi?: TcfApi }).__tcfapi;
  if (typeof api !== "function") {
    ready = Promise.resolve();
    return ready;
  }
  // A CMP is on the page but has not answered: no identifier until it does.
  current = { storage: false };
  ready = new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, FIRST_SIGNAL_MS);
    try {
      api("addEventListener", 2, (data, ok) => {
        if (!ok || !data) return;
        current = toConsent(data);
        clearTimeout(timer);
        resolve();
      });
    } catch {
      clearTimeout(timer);
      resolve();
    }
  });
  return ready;
}

export function currentConsent(): Consent {
  return current;
}

export function resetConsentForTest(): void {
  current = { storage: true };
  ready = null;
}
